<template>
	<div class="value-cell">
		<FilterValueInput
			v-if="hasOperator"
			:field="field"
			:operator="filterData.operator"
			:value="filterData.value"
			:disabled="!userFullPermissions"
			@input="handleValueChange"
		/>
		<span
			v-else
			class="cell-display empty-placeholder"
		>
			{{ displayValue }}
		</span>
	</div>
</template>

<script>
import FilterValueInput from './FilterValueInput.vue'

export default {
	name: 'filter-value-cell',
	components: {
		FilterValueInput
	},
	props: {
		field: {
			type: Object,
			required: true
		},
		filterData: {
			type: Object,
			required: true
		},
		editingCell: {
			type: Object,
			default: null
		},
		userFullPermissions: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		hasOperator () {
			return !!(this.filterData.operator)
		},
		displayValue () {
			return this.hasOperator ? this.getFormattedDisplayValue(this.field, this.filterData) : 'Select operator first'
		}
	},
	methods: {
		handleValueChange (value) {
			this.$emit('filter-change', {
				fieldName: this.field.fieldName,
				field: 'value',
				value: value
			})
		},
		getFormattedDisplayValue (field, filterData) {
			if (!filterData.value) return 'Filter value'

			const value = filterData.value
			const operator = (filterData.operator || '').toString().toLowerCase()
			const dataType = field.typeFriendlyName || field.dataType || ''
			const isDateType = ['datetime', 'date', 'time', 'timestamp'].includes(dataType.toLowerCase())

			// Handle array values (BETWEEN, IN operators)
			if (Array.isArray(value)) {
				if (operator === 'BETWEEN' || operator === 'NOT BETWEEN') {
					const fromValue = isDateType ? this.formatDate(value[0]) : value[0]
					const toValue = isDateType ? this.formatDate(value[1]) : value[1]
					if (!fromValue && !toValue) return 'Select range'
					if (!fromValue) return `up to ${toValue}`
					if (!toValue) return `from ${fromValue}`
					return `${fromValue} and ${toValue}`
				}
				// For multi-value operators, render as comma-separated list
				if (operator === 'includes' || operator === 'does not include' || operator === 'in' || operator === 'not in') {
					return value.filter(v => v != null && v !== '').join(', ')
				}
				// Fallback: show first value
				return Array.isArray(value) ? (value[0] || '') : value
			}

			// Handle single values
			if (isDateType) {
				return this.formatDate(value)
			}

			// Handle boolean values
			if (['bool', 'boolean'].includes(dataType.toLowerCase())) {
				return value === '1' || value === true ? 'True' : 'False'
			}

			return value
		},
		formatDate (dateValue) {
			if (!dateValue) return ''
			try {
				const date = new Date(dateValue)
				if (isNaN(date.getTime())) return dateValue
				// Format as a more readable date: Jan 15, 2024
				return date.toLocaleDateString('en-US', {
					year: 'numeric',
					month: 'short',
					day: 'numeric'
				})
			} catch (error) {
				return dateValue
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.value-cell {
	padding: 8px !important;
	vertical-align: middle;
	position: relative;
}

.cell-display {
	display: block;
	padding: 8px 12px;
	min-height: 16px;
	line-height: 16px;
	font-size: 13px;
	width: 100%;
	box-sizing: border-box;
	border-radius: var(--radius-sm, 4px);
	text-align: center;

	&.empty-placeholder {
		color: var(--global-muted-foreground);
		font-style: italic;
	}
}

// Ensure all value inputs have consistent small sizing to match operator dropdown
:deep(.v-input) {
	font-size: 13px;
}

:deep(.v-input__slot) {
	min-height: 32px !important;
	padding: 0 8px !important;
}

:deep(.v-text-field__details) {
	display: none !important;
}
</style>
